import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useCreatePrice } from "./use-create-price";
import { useProductsList } from "./use-products-list";
import {
  PRICE_CURRENCY_REGEX,
  PRICE_LOOKUP_KEY_MAX_LENGTH,
  PRICE_LOOKUP_KEY_REGEX,
  PRICE_UNIT_AMOUNT_MAX,
} from "./constants";
import { unitAmountFromMajorString } from "./format-money";
import {
  PRICE_INTERVAL_UNITS,
  isIntervalCountValid,
  parseIntervalCount,
  type PriceIntervalUnit,
} from "./price-interval";

interface CreatePriceDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

const DEFAULT_CURRENCY = "usd";
const DEFAULT_INTERVAL_UNIT: PriceIntervalUnit = "month";
const DEFAULT_INTERVAL_COUNT = "1";

export function CreatePriceDialog({ open, onOpenChange, onSuccess }: CreatePriceDialogProps) {
  const { t } = useTranslation();
  const { data: products } = useProductsList();
  const [productId, setProductId] = useState("");
  const [lookupKey, setLookupKey] = useState("");
  const [unitAmountInput, setUnitAmountInput] = useState("");
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [intervalUnit, setIntervalUnit] = useState<PriceIntervalUnit>(DEFAULT_INTERVAL_UNIT);
  const [intervalCountInput, setIntervalCountInput] = useState(DEFAULT_INTERVAL_COUNT);
  const [error, setError] = useState<string | null>(null);
  const create = useCreatePrice();

  useEffect(() => {
    if (open) {
      setProductId("");
      setLookupKey("");
      setUnitAmountInput("");
      setCurrency(DEFAULT_CURRENCY);
      setIntervalUnit(DEFAULT_INTERVAL_UNIT);
      setIntervalCountInput(DEFAULT_INTERVAL_COUNT);
      setError(null);
    }
  }, [open]);

  const activeProducts = useMemo(() => (products ?? []).filter((p) => p.active), [products]);
  const noActiveProducts = products !== undefined && activeProducts.length === 0;

  const trimmedLookupKey = lookupKey.trim();
  const trimmedCurrency = currency.trim().toLowerCase();
  const cents = unitAmountFromMajorString(unitAmountInput, trimmedCurrency);

  const lookupKeyValid =
    trimmedLookupKey.length > 0 && PRICE_LOOKUP_KEY_REGEX.test(trimmedLookupKey);
  const currencyValid = PRICE_CURRENCY_REGEX.test(trimmedCurrency);
  const amountValid = cents !== null && cents <= PRICE_UNIT_AMOUNT_MAX;
  const productValid = productId.length > 0;
  const amountInputDirty = unitAmountInput.trim().length > 0;
  const showAmountError = amountInputDirty && !amountValid;

  const intervalCount = parseIntervalCount(intervalCountInput);
  const intervalCountValid = isIntervalCountValid(intervalCountInput);
  const intervalCountDirty = intervalCountInput.trim().length > 0;
  const showIntervalCountError = intervalCountDirty && !intervalCountValid;

  const isValid =
    lookupKeyValid && currencyValid && amountValid && productValid && intervalCountValid;

  const handleClose = (next: boolean) => {
    if (!next && create.isPending) return;
    onOpenChange(next);
  };

  const handleSubmit = () => {
    if (!isValid || cents === null || intervalCount === null) return;
    setError(null);
    create.mutate(
      {
        productId,
        lookupKey: trimmedLookupKey,
        unitAmount: cents,
        currency: trimmedCurrency,
        recurring: { interval: intervalUnit, intervalCount },
      },
      {
        onSuccess: () => onSuccess(),
        onError: (err) => {
          setError(
            resolveMutationErrorMessage(err, t, {
              forbiddenKey: "catalog.errors.forbidden",
              serverPrefixKey: "catalog.errors.serverPrefix",
              genericKey: "catalog.prices.create.error",
            }),
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="create-price-dialog">
        <DialogHeader>
          <DialogTitle>{t("catalog.prices.create.title")}</DialogTitle>
          <DialogDescription>{t("catalog.prices.create.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <label
              htmlFor="create-price-product"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("catalog.prices.fields.product")}
            </label>
            <select
              id="create-price-product"
              data-testid="create-price-product-input"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              disabled={create.isPending || noActiveProducts}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            >
              <option value="">{t("catalog.prices.create.productPlaceholder")}</option>
              {activeProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
            {noActiveProducts && (
              <p
                data-testid="create-price-no-products"
                className="font-sans text-xs font-normal text-muted-foreground"
              >
                {t("catalog.prices.create.noActiveProducts")}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="create-price-lookup-key"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("catalog.prices.fields.lookupKey")}
            </label>
            <Input
              id="create-price-lookup-key"
              data-testid="create-price-lookup-key-input"
              value={lookupKey}
              onChange={(e) => setLookupKey(e.target.value)}
              maxLength={PRICE_LOOKUP_KEY_MAX_LENGTH}
              disabled={create.isPending}
              placeholder={t("catalog.prices.create.lookupKeyPlaceholder")}
              autoComplete="off"
            />
            <p className="font-sans text-xs font-normal text-muted-foreground">
              {t("catalog.prices.create.lookupKeyHint")}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label
                htmlFor="create-price-amount"
                className="font-sans text-sm font-medium text-foreground"
              >
                {t("catalog.prices.fields.amount")}
              </label>
              <Input
                id="create-price-amount"
                data-testid="create-price-amount-input"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={unitAmountInput}
                onChange={(e) => setUnitAmountInput(e.target.value)}
                disabled={create.isPending}
                placeholder={t("catalog.prices.create.amountPlaceholder")}
                aria-invalid={showAmountError || undefined}
                aria-describedby="create-price-amount-hint"
              />
              <p
                id="create-price-amount-hint"
                className="font-sans text-xs font-normal text-muted-foreground"
              >
                {t("catalog.prices.create.amountHint")}
              </p>
              {showAmountError && (
                <p
                  data-testid="create-price-amount-error"
                  role="alert"
                  className="font-sans text-xs font-medium text-destructive"
                >
                  {t("catalog.prices.create.amountError")}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="create-price-currency"
                className="font-sans text-sm font-medium text-foreground"
              >
                {t("catalog.prices.fields.currency")}
              </label>
              <Input
                id="create-price-currency"
                data-testid="create-price-currency-input"
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toLowerCase())}
                maxLength={3}
                disabled={create.isPending}
                placeholder={t("catalog.prices.create.currencyPlaceholder")}
                autoCapitalize="none"
                autoComplete="off"
              />
              <p className="font-sans text-xs font-normal text-muted-foreground">
                {t("catalog.prices.create.currencyHint")}
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <span className="font-sans text-sm font-medium text-foreground">
              {t("catalog.prices.fields.billingInterval")}
            </span>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label
                  htmlFor="create-price-interval-count"
                  className="font-sans text-xs font-normal text-muted-foreground"
                >
                  {t("catalog.prices.fields.intervalCount")}
                </label>
                <Input
                  id="create-price-interval-count"
                  data-testid="create-price-interval-count-input"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  step="1"
                  value={intervalCountInput}
                  onChange={(e) => setIntervalCountInput(e.target.value)}
                  disabled={create.isPending}
                  aria-invalid={showIntervalCountError || undefined}
                />
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor="create-price-interval-unit"
                  className="font-sans text-xs font-normal text-muted-foreground"
                >
                  {t("catalog.prices.fields.intervalUnit")}
                </label>
                <select
                  id="create-price-interval-unit"
                  data-testid="create-price-interval-unit-input"
                  value={intervalUnit}
                  onChange={(e) => setIntervalUnit(e.target.value as PriceIntervalUnit)}
                  disabled={create.isPending}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                >
                  {PRICE_INTERVAL_UNITS.map((unit) => (
                    <option key={unit} value={unit}>
                      {t(`catalog.prices.intervalUnitOption.${unit}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="font-sans text-xs font-normal text-muted-foreground">
              {t("catalog.prices.create.intervalHint")}
            </p>
            {showIntervalCountError && (
              <p
                data-testid="create-price-interval-count-error"
                role="alert"
                className="font-sans text-xs font-medium text-destructive"
              >
                {t("catalog.prices.create.intervalCountError")}
              </p>
            )}
          </div>

          {error && (
            <p
              data-testid="create-price-error"
              role="alert"
              className="font-sans text-sm font-medium text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={create.isPending}>
            {t("catalog.actions.cancel")}
          </Button>
          <Button
            data-testid="confirm-create-price-button"
            onClick={handleSubmit}
            disabled={!isValid || create.isPending}
          >
            {create.isPending
              ? t("catalog.prices.create.pending")
              : t("catalog.prices.actions.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
